import { css } from "@emotion/react"
import gql from "graphql-tag"
import React, { useCallback, useState } from "react"
import toastr from "toastr"
import { useMutation } from "../../../../api/graphql"
import { Event } from "../../../../models"
import Modal from "../../misc/Modal"

const SubmitDuplicateInquiry = gql`
  mutation SubmitDuplicateInquiry($eventId: ID!, $message: String!) {
    duplicateInquirySubmit(eventId: $eventId, message: $message) {
      success
      errors
    }
  }
`

type Props = {
  event: Event
  isOpen: boolean
  onClose: () => void
}

const DuplicateInquiryModal: React.FC<Props> = ({ event, isOpen, onClose }) => {
  const [message, setMessage] = useState<string>("")
  const [submitMutation, { loading: mutationLoading }] = useMutation(SubmitDuplicateInquiry)

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!message.trim()) {
        toastr.warning("Please enter a message explaining why this is not a duplicate.")
        return
      }

      submitMutation({ variables: { eventId: event.id, message: message.trim() } })
        .then(({ data }) => {
          const response = data?.duplicateInquirySubmit
          if (response?.success) {
            toastr.success("Your message has been sent. We'll review it and get back to you.")
            setMessage("")
            onClose()
          } else {
            const errorMsg = response?.errors?.[0] || "There was an error sending your message."
            toastr.error(errorMsg)
          }
        })
        .catch((error) => {
          console.error("Duplicate inquiry error:", error)
          toastr.error("There was an error sending your message. Please try again.")
        })
    },
    [event.id, message, onClose, submitMutation]
  )

  const handleClose = useCallback(() => {
    setMessage("")
    onClose()
  }, [onClose])

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose}>
      <h2
        css={css`
          margin: 0 0 8px 0;
          font-size: 20px;
        `}
      >
        Report Incorrect Duplicate
      </h2>
      <p
        css={css`
          margin: 0 0 16px 0;
          color: #666;
          font-size: 14px;
        `}
      >
        Let us know why &ldquo;{event.title}&rdquo; should not be marked as a duplicate.
      </p>

      <form onSubmit={handleFormSubmit}>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Please explain why this event is unique and should not be considered a duplicate. For example: 'This is a different event on the same day' or 'I am the organizer of this event.'"
          rows={5}
          css={css`
            width: 100%;
            padding: 12px;
            font-size: 14px;
            border: 1px solid #ddd;
            border-radius: 4px;
            resize: vertical;
            min-height: 120px;
            box-sizing: border-box;
            &:focus {
              outline: none;
              border-color: #1976d2;
            }
          `}
        />

        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 16px;
          `}
        >
          <button
            type="button"
            className="btn btn-grey"
            onClick={handleClose}
            disabled={mutationLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-blue"
            disabled={mutationLoading || !message.trim()}
          >
            {mutationLoading ? "Sending..." : "Send Message"}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default DuplicateInquiryModal
